import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Search, Download, MapPin } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const NAVY = "#0F2C59";
const shortIl = (s) => (s || "").replace(/\s*T[İI]CARET S[İI]C[İI]L[İI] M[ÜU]D[ÜU]RL[ÜU][ĞG][ÜU]\s*/i, "").trim() || "—";

export default function SicilDistribution() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/sicil-distribution")
      .then(({ data }) => setRows(data.rows || []))
      .catch(() => setRows([]));
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLocaleLowerCase("tr");
    const list = (rows || []).filter((r) => !t || shortIl(r.tsm).toLocaleLowerCase("tr").includes(t));
    return showAll ? list : list.slice(0, 10);
  }, [rows, q, showAll]);

  const totals = useMemo(() => (rows || []).reduce(
    (a, r) => ({ firms: a.firms + r.firms, kesin: a.kesin + r.kesin, belge: a.belge + r.belge }),
    { firms: 0, kesin: 0, belge: 0 }), [rows]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/reports/sicil-distribution/export", { params: { format: "xlsx" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "SİCİL İLİ DAĞILIMI.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally { setBusy(false); }
  };

  if (!rows) return <Skeleton className="h-64 w-full" />;

  const chart = (rows || []).slice(0, 10).map((r) => ({ il: shortIl(r.tsm), Firma: r.firms, Kesin: r.kesin }));

  return (
    <div className="space-y-4" data-testid="sicil-distribution">
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <MapPin className="h-4 w-4" style={{ color: NAVY }} />
          <p className="text-sm font-medium">Sicil İli Dağılımı</p>
          <span className="text-xs text-muted-foreground" data-testid="sicil-totals">
            {rows.length} il · {totals.firms} FAAL firma · {totals.kesin} kesin oy · {totals.belge} yetki belgesi
          </span>
          <div className="relative ml-auto">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-8 h-9 w-[200px]" placeholder="İl ara..." value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="sicil-search" />
          </div>
          <Button variant="outline" size="sm" className="h-9" disabled={busy} onClick={download} data-testid="sicil-export">
            <Download className="h-4 w-4 mr-1.5" />Excel
          </Button>
        </div>

        <div className="h-[220px] w-full" data-testid="sicil-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 28 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
              <XAxis dataKey="il" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="Firma" fill={NAVY} radius={[3, 3, 0, 0]} />
              <Bar dataKey="Kesin" fill="#16A34A" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm" data-testid="sicil-table">
            <thead className="bg-muted/60">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2 font-medium">Sicil İli</th>
                <th className="px-3 py-2 font-medium text-right">Firma</th>
                <th className="px-3 py-2 font-medium text-right">Kesin Oy</th>
                <th className="px-3 py-2 font-medium text-right">Yetki Belgesi</th>
                <th className="px-3 py-2 font-medium text-right">Kesin Oranı</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.tsm} className="border-t border-border/60" data-testid={`sicil-row-${shortIl(r.tsm)}`}>
                  <td className="px-3 py-2 font-medium">{shortIl(r.tsm)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.firms}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-600">{r.kesin}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.belge}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    %{r.firms ? Math.round((r.kesin / r.firms) * 100) : 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!q && rows.length > 10 && (
          <Button variant="ghost" size="sm" className="h-8" onClick={() => setShowAll((s) => !s)} data-testid="sicil-toggle-all">
            {showAll ? "İlk 10'u göster" : `Tümünü gör (${rows.length} il)`}
          </Button>
        )}
        <p className="text-xs text-muted-foreground">
          Sicil ili, firmanın Ticaret Sicili Müdürlüğü bilgisinden alınır. Yalnız FAAL firmalar sayılır;
          kesin oy = 2026 tahmini KESİN olanlar.
        </p>
      </Card>
    </div>
  );
}
